
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { CalendarClock, Phone, MessageSquare, AlertTriangle, CheckCircle2, InfoIcon, ArrowRight } from "lucide-react";
import { AISummary, CallTranscript, ContextProfile, Lead } from "@/types";
import { getLeadCallFlowData, processLeadCallFlow } from "@/services/ai/callFlowService";
import { toast } from "@/components/ui/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";

interface LeadCallFlowProps {
  lead: Lead;
  voiceId?: string;
  onCallComplete?: () => void;
}

const LeadCallFlow: React.FC<LeadCallFlowProps> = ({ lead, voiceId, onCallComplete }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("context");
  const [progress, setProgress] = useState(0);
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["leadCallFlow", lead.id],
    queryFn: () => lead.id ? getLeadCallFlowData(lead.id) : Promise.resolve({
      contextProfile: undefined,
      callTranscript: undefined,
      aiSummary: undefined
    }),
    enabled: !!lead.id
  });
  
  const contextProfile = data?.contextProfile;
  const callTranscript = data?.callTranscript;
  const aiSummary = data?.aiSummary;
  
  // Calculate current step based on available data
  const currentStep = !contextProfile ? 0 : !callTranscript ? 1 : !aiSummary ? 2 : 3;
  
  useEffect(() => {
    // Set progress based on current step
    setProgress(((currentStep + 1) / 4) * 100);
  }, [currentStep]);
  
  const handleStartCallFlow = async () => {
    if (!lead.id) {
      toast({
        title: "Error",
        description: "Lead ID is missing",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const result = await processLeadCallFlow(lead, voiceId);
      
      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Call flow initiated successfully"
        });
        
        // Refresh data
        await refetch();
        
        if (onCallComplete) {
          onCallComplete();
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error processing call flow:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const getIntentColor = (intent?: string) => {
    if (!intent) return "gray";
    
    if (intent.toLowerCase().includes("hot")) return "red";
    if (intent.toLowerCase().includes("warm")) return "orange";
    if (intent.toLowerCase().includes("cold")) return "blue";
    
    return "gray";
  };
  
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load call flow data: {error instanceof Error ? error.message : "Unknown error"}
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-2xl font-bold tracking-tight">Lead Reactivation Flow</h3>
        <p className="text-muted-foreground">
          AI-powered call flow to reactivate {lead.name}
        </p>
      </div>
      
      {/* Progress bar and steps */}
      <div className="space-y-2">
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <div className={currentStep >= 0 ? "font-medium text-primary" : ""}>Pre-Call AI</div>
          <div className={currentStep >= 1 ? "font-medium text-primary" : ""}>Call</div>
          <div className={currentStep >= 2 ? "font-medium text-primary" : ""}>Analysis</div>
          <div className={currentStep >= 3 ? "font-medium text-primary" : ""}>Results</div>
        </div>
      </div>
      
      {/* Call flow action card */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Reactivation Status</CardTitle>
          <CardDescription>
            Current status: <Badge variant="outline">{lead.status}</Badge>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {currentStep === 3 && aiSummary?.intent_score && (
                <Alert className={`bg-${getIntentColor(aiSummary.intent_score)}-50 border-${getIntentColor(aiSummary.intent_score)}-200`}>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Lead Intent: <Badge>{aiSummary.intent_score}</Badge></AlertTitle>
                  <AlertDescription>
                    {aiSummary.next_action}
                  </AlertDescription>
                </Alert>
              )}
              
              {currentStep < 3 && (
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Call Flow Status</AlertTitle>
                  <AlertDescription>
                    {currentStep === 0 && "Ready to start the AI-powered call flow"}
                    {currentStep === 1 && "Pre-call analysis complete, ready to make the call"}
                    {currentStep === 2 && "Call complete, analyzing results"}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleStartCallFlow} 
            disabled={isProcessing || isLoading}
            className="w-full"
          >
            {isProcessing ? (
              <>Processing...</>
            ) : currentStep === 0 ? (
              <>Start Call Flow</>
            ) : currentStep === 3 ? (
              <>Restart Call Flow</>
            ) : (
              <>Continue Call Flow</>
            )}
          </Button>
        </CardFooter>
      </Card>
      
      {/* Call flow data tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="context">Pre-Call Context</TabsTrigger>
          <TabsTrigger value="transcript">Call Transcript</TabsTrigger>
          <TabsTrigger value="summary">AI Summary</TabsTrigger>
        </TabsList>
        
        <TabsContent value="context" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <InfoIcon className="h-5 w-5 mr-2" />
                Lead Context Profile
              </CardTitle>
              <CardDescription>
                AI-generated insights to personalize the call
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : contextProfile ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-1">Summary</h4>
                    <p className="text-sm text-muted-foreground">{contextProfile.summary || "No summary available"}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-1">Suggested Tone</h4>
                      <Badge>{contextProfile.tone || "Not specified"}</Badge>
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Vehicle Interest</h4>
                      <p className="text-sm">{contextProfile.vehicle_interest || "Unknown"}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-1">Next Best Action</h4>
                    <p className="text-sm">{contextProfile.next_best_action || "No specific action recommended"}</p>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    Generated by {contextProfile.ai_model || "AI"} · 
                    {contextProfile.created_at && ` ${formatDistanceToNow(new Date(contextProfile.created_at), { addSuffix: true })}`}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No context profile generated yet</p>
                  <p className="text-sm mt-1">Start the call flow to generate insights</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="transcript" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Phone className="h-5 w-5 mr-2" />
                Call Transcript
              </CardTitle>
              <CardDescription>
                Full transcript of the conversation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : callTranscript?.transcript ? (
                <div className="space-y-4">
                  <div className="bg-muted p-4 rounded-md max-h-[300px] overflow-y-auto">
                    <p className="whitespace-pre-line text-sm">{callTranscript.transcript}</p>
                  </div>
                  
                  {callTranscript.audio_url && (
                    <div>
                      <h4 className="font-medium mb-2">Call Audio</h4>
                      <audio controls className="w-full">
                        <source src={callTranscript.audio_url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                  
                  <div className="text-xs text-muted-foreground">
                    Call recorded {callTranscript.created_at && formatDistanceToNow(new Date(callTranscript.created_at), { addSuffix: true })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No call transcript available</p>
                  <p className="text-sm mt-1">Start the call flow to make a call</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                AI Analysis
              </CardTitle>
              <CardDescription>
                AI-generated summary and next steps
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : aiSummary ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Intent Score</h4>
                    <Badge className={`bg-${getIntentColor(aiSummary.intent_score)}-100 text-${getIntentColor(aiSummary.intent_score)}-600 hover:bg-${getIntentColor(aiSummary.intent_score)}-200`}>
                      {aiSummary.intent_score || "Unknown"}
                    </Badge>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium mb-1">Summary</h4>
                    <p className="text-sm">{aiSummary.summary || "No summary available"}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-1">Recommended Next Action</h4>
                    <div className="flex items-center bg-muted p-3 rounded-md">
                      <CalendarClock className="h-4 w-4 mr-2 flex-shrink-0" />
                      <p className="text-sm">{aiSummary.next_action || "No specific action recommended"}</p>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    Analyzed by {aiSummary.model_used || "AI"} · 
                    {aiSummary.created_at && ` ${formatDistanceToNow(new Date(aiSummary.created_at), { addSuffix: true })}`}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No AI summary generated yet</p>
                  <p className="text-sm mt-1">Complete the call to get AI analysis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadCallFlow;
